import type { ApiError, ApiErrorDetail } from '@t4s/api-client/model'

/**
 * A failure, as the rest of this application handles one.
 *
 * The API answers every failure with a flat `{ code, message }` plus `details[]` on a
 * validation failure (docs/ARCHITECTURE.md §4). **This surface switches on `code` and
 * never displays `message`** — see `messages.ts` and docs/ux/PLATFORM-ADMIN.md §20.1.
 * The prose the API sends is written for a developer reading a log and varies per
 * deployment; **MF6** and **LB4** are what rendering it costs.
 *
 * Everything that can go wrong arrives here as one of these, including the failures that
 * never reach the network (§20.3), so a screen has exactly one shape to handle.
 */
export class ApiFailure extends Error {
  /** The stable, machine-readable code. Never shown; always what copy is chosen by. */
  readonly code: string
  /** The HTTP status, or 0 when the request never got an answer. */
  readonly status: number
  /** Field-level failures, each with a dotted path, on `validation_failed`. */
  readonly details: readonly ApiErrorDetail[]

  constructor(code: string, status: number, details: readonly ApiErrorDetail[] = []) {
    // The `Error` message is for a stack trace, never for a screen. It carries the code
    // rather than the API's prose so that nothing can render the wrong half by accident.
    super(`API failure: ${code}`)
    this.name = 'ApiFailure'
    this.code = code
    this.status = status
    this.details = details
  }
}

/** Codes this client raises for itself, because the request never got an answer (§20.3). */
export const CLIENT_OFFLINE = 'client_offline'
export const CLIENT_TIMEOUT = 'client_timeout'
export const CLIENT_UNKNOWN = 'client_unknown'

/** What the API sends when nothing else fits, and what an empty body degrades to. */
export const UNKNOWN_CODE = 'unknown'

/**
 * The response shape every generated endpoint function resolves to. Narrowed here rather
 * than imported: orval names one union member per documented status, and this layer only
 * needs the two fields they all share.
 */
export type ApiResult = {
  readonly status: number
  readonly data: unknown
}

/** True for a status this application treats as success. */
export function isSuccess(result: ApiResult): boolean {
  return result.status >= 200 && result.status < 300
}

/**
 * A failed response as an `ApiFailure`.
 *
 * A body that is not the error envelope — a proxy's HTML error page, an empty 502 — still
 * has to produce a code, so the status stands in for one. Nothing downstream may ever
 * receive `undefined` and have to decide what it means.
 */
export function failureOf(result: ApiResult): ApiFailure {
  const body = (result.data ?? {}) as Partial<ApiError>
  const code = typeof body.code === 'string' && body.code !== '' ? body.code : UNKNOWN_CODE

  return new ApiFailure(code, result.status, body.details ?? [])
}

/**
 * A thrown value as an `ApiFailure` — the request that could not be sent, or was sent and
 * answered by nothing.
 *
 * `fetch` rejects with a `TypeError` when the network refuses it, and with an
 * `AbortError` when something cancelled or timed it out. Both are ordinary states on a
 * laptop whose lid was shut, and both must read as a sentence with a retry rather than as
 * a form that silently does nothing (§4.2).
 */
export function failureFromThrown(thrown: unknown): ApiFailure {
  if (thrown instanceof ApiFailure) {
    return thrown
  }
  if (isAbort(thrown)) {
    return new ApiFailure(CLIENT_TIMEOUT, 0)
  }
  if (thrown instanceof TypeError) {
    return new ApiFailure(CLIENT_OFFLINE, 0)
  }

  return new ApiFailure(CLIENT_UNKNOWN, 0)
}

function isAbort(thrown: unknown): boolean {
  return thrown instanceof Error && (thrown.name === 'AbortError' || thrown.name === 'TimeoutError')
}

/**
 * The one place a generated call becomes either data or a thrown `ApiFailure`.
 *
 * The transport deliberately returns an error response as data rather than throwing
 * (`packages/api-client/src/fetch.ts`), because that decision belongs to the query layer.
 * This is the query layer making it, once.
 */
/**
 * A caught value's stable code, or `client_unknown` for anything that is not an
 * `ApiFailure`.
 *
 * Every screen's own error handling reduces to this one line, and it used to be copied —
 * `tenant-page.tsx`, `tenant-list.tsx` and every P03 screen each wrote their own — which
 * meant the "not actually an `ApiFailure`" branch was untested in each copy alike, since
 * `unwrap` above is the only path an error takes to a screen and it never produces
 * anything else. One definition, tested once here, is what a `catch` block or a
 * `useMutation`'s `onError` should read the code from.
 */
export function codeOf(error: unknown): string {
  return error instanceof ApiFailure ? error.code : CLIENT_UNKNOWN
}

export async function unwrap<Data>(call: Promise<ApiResult>): Promise<Data> {
  let result: ApiResult
  try {
    result = await call
  } catch (thrown) {
    throw failureFromThrown(thrown)
  }

  if (!isSuccess(result)) {
    throw failureOf(result)
  }

  return result.data as Data
}

/**
 * The field key inside a `validation_failed` detail's `path`, with the request-location
 * prefix removed.
 *
 * `docs/DECISIONS.md`, "A service-level field error's `details[].path` matches the location
 * Zod would give the same field": every path is prefixed with where the value was read from
 * — `json`, `query`, `param`, `header`, or `body` for the one raw-byte-body route that has
 * no JSON to name — before any of the schema's own field names. This strips exactly that
 * first segment, whatever it is, `chore-platform-field-error-mapping`'s equivalent of
 * `apps/app`'s `topLevelField` (`chore-app-field-error-path-mismatch`).
 *
 * **It keeps everything past that first segment, joined back together — not only the next
 * one.** `apps/app`'s `topLevelField` keeps only the immediate next segment, because every
 * deeper segment it ever sees is an array index into the *same* on-screen field
 * (`json.dates.0` is the `dates` field, exactly as `json.dates` is). `apps/platform`'s own
 * nested paths are different in kind: `POST /platform/tenants`'s `json.superadmin.nickname`
 * and `json.superadmin.fullName` are **two different form fields** — the create-tenant page
 * has a Nickname input and a separate Full name input, and collapsing both onto one
 * `superadmin` key would mark the wrong field, or leave no way to tell which of the two
 * failed. Keeping the whole remainder as the field key (`superadmin.nickname`,
 * `superadmin.fullName`) is this task's deliberate choice, recorded in `docs/DECISIONS.md`.
 * A flat path such as `json.name` or `json.nickname` (`POST
 * /platform/tenants/{tenantId}/superadmins`'s own, unnested schema) is unaffected either
 * way — there is only one segment after the prefix, so both algorithms agree.
 */
function fieldKeyOf(path: string): string {
  const segments = path.split('.')
  return segments.length > 1 ? segments.slice(1).join('.') : ''
}

/**
 * The field keys a `validation_failed`'s `details[]` names, prefix stripped exactly as
 * `fieldErrorsOf` strips it.
 *
 * For a caller that only needs to know *whether* one of a handful of paths was named,
 * rather than resolve each path to its own field and its own copy — mirrors `apps/app`'s
 * `fieldsOf` (`chore-app-field-error-path-mismatch`).
 */
export function fieldsOf(failure: ApiFailure): ReadonlySet<string> {
  return new Set(failure.details.map((detail) => fieldKeyOf(detail.path)))
}

/**
 * The field errors carried by a `validation_failed`, as a map from the field keys a caller
 * recognises to one line each.
 *
 * `docs/ux/PLATFORM-ADMIN.md` §20.1: "a path the screen recognises marks that field, and one
 * it does not becomes a form-level message" — `fields` is what a form recognises, keyed on
 * {@link fieldKeyOf}'s own bare key, and everything else is dropped here rather than
 * rendered, because a server path with no field to attach it to has nowhere to go but a
 * message this console never shows (§20.1: the API's own `message` is never rendered).
 *
 * Mirrors `apps/app`'s `fieldErrorsOf` (`chore-app-field-error-path-mismatch`) exactly in
 * shape and behaviour — same signature, same "first error per field wins" rule, same total
 * function that never throws on an unrecognised or malformed path — differing only in how
 * {@link fieldKeyOf} resolves a nested path, for the reason its own doc comment gives.
 */
export function fieldErrorsOf<Field extends string>(
  failure: ApiFailure,
  fields: Readonly<Record<string, Field>>,
  copy: (field: Field) => string,
): Partial<Record<Field, string>> {
  const errors: Partial<Record<Field, string>> = {}

  for (const detail of failure.details) {
    const field = fields[fieldKeyOf(detail.path)]
    if (field !== undefined && errors[field] === undefined) {
      errors[field] = copy(field)
    }
  }

  return errors
}
